import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import SearchableSelect from './SearchableSelect';

const NewInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const isViewMode = window.location.pathname.includes('/view/');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  //Ref for scrolling to error/success messages
  const messageRef = useRef(null);

  // Form states
  const [customers, setCustomers] = useState([]);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [filmSizes, setFilmSizes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [missingNumbers, setMissingNumbers] = useState([]);

  const [noUnbilledReportsMessage, setNoUnbilledReportsMessage] = useState('');

  // States dropdown
  const indianStates = [
    'Maharashtra', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
    'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
    'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  //Pagination for reports - increased per page
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(50);

  // Financial years
  const [financialYears, setFinancialYears] = useState([]);

  // Initial form state helper
  const getInitialFormState = () => ({
    // Basic info
    customer: '',
    invoiceType: 'Film',
    financialYear: getCurrentFinancialYear(),
    invoiceDate: new Date().toISOString().split('T')[0],
    jobName: '',
    poNumber: '',
    soNumber: '',
    projectNumber: '',
    invoiceNumber: '',

    contactPerson: '',
    noteRemark: '',
    cgst: 9,
    sgst: 9,

    // Billing Address
    billingCustomerName: '',
    billingAddress: '',
    billingGST: '',
    billingState: 'Maharashtra',

    // Shipping Address
    shippingCustomerName: '',
    shippingAddress: '',
    shippingGST: '',
    shippingState: 'Maharashtra',

    // Convenience Charges
    minChargesQty: '',
    minChargesRate: '',
    minChargesHSN: '998346',

    numberOfVisits: '',
    perVisitRate: '',
    visitsHSN: '998346',

    // Date-based Charges
    selectedDates: [],
    datesQuantity: '1',
    datesCharges: '',
    datesHSN: '998346',

    // Other Charges
    otherCharges: [
      {
        id: 1,
        chargeName: '',
        quantity: 1,
        rate: 0
      }
    ],

    // Report Filters
    filterPO: '',
    filterMSN: '',
    filterTag: '',
    filterDrawing: '',
    filterSSN: '',
    filterProject: '',
    filterSO: '',
    filterJobId: '',
    filterReportNumber: '',
    filterFinancialYear: '',
    filterCreatedBy: '',
    filterFromDate: '',
    filterToDate: '',
    showOnlySelected: isEditMode,

    // Selected Reports
    selectedReports: [],

    // Site Work Items
    siteWorkItems: [
      {
        id: 1,
        date: '',
        description: '',
        quantity: 1,
        rate: 0
      }
    ]
  });

  const [formData, setFormData] = useState(getInitialFormState());

  // Helper function to get current financial year
  function getCurrentFinancialYear() {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    if (currentMonth >= 4) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  }

  // Generate financial years list
  function generateFinancialYears() {
    const years = [];
    const currentYear = new Date().getFullYear();

    for (let year = 2020; year <= currentYear + 1; year++) {
      years.push(`${year}-${year + 1}`);
    }

    return years;
  }

  //Helper function to format date from ISO to YYYY-MM-DD (UTC)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    console.log('🔧 formatDateForInput input:', dateString, 'Type:', typeof dateString);

    // If it's already a Date object, convert to ISO string first
    if (dateString instanceof Date) {
      const year = dateString.getFullYear();
      const month = String(dateString.getMonth() + 1).padStart(2, '0');
      const day = String(dateString.getDate()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}`;
      console.log('🔧 Formatted from Date object:', formatted);
      return formatted;
    }

    // Convert to string
    let dateStr = dateString.toString();

    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      console.log('🔧 Already in correct format:', dateStr);
      return dateStr;
    }

    // If contains 'T' (ISO format), extract date part
    if (dateStr.includes('T')) {
      const formatted = dateStr.split('T')[0];
      console.log('🔧 Extracted from ISO format:', formatted);
      return formatted;
    }

    // If contains time with space, extract date part
    if (dateStr.includes(' ')) {
      const formatted = dateStr.split(' ')[0];
      console.log('🔧 Extracted from space-separated format:', formatted);
      return formatted;
    }

    // Try parsing as date object
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        console.log('🔧 Parsed and formatted:', formatted);
        return formatted;
      }
    } catch (e) {
      console.error('🔧 Error formatting date:', dateStr, e);
    }

    console.error('🔧 Could not format date:', dateStr);
    return '';
  };

  useEffect(() => {
    fetchCustomers();
    fetchReports();
    fetchFilmSizes();
    fetchEmployees();
    setFinancialYears(generateFinancialYears());

    if (isEditMode || isViewMode) {
      fetchInvoiceData();
    }
  }, [id]);

  // Auto-generate invoice number when financial year changes
  useEffect(() => {
    if (!isEditMode && formData.financialYear) {
      fetchNextInvoiceNumber();
      fetchMissingInvoiceNumbers(formData.financialYear);
    }
  }, [formData.financialYear]);

  useEffect(() => {
    if (isEditMode && formData.financialYear) {
      fetchMissingInvoiceNumbers(formData.financialYear);
    }
  }, [formData.financialYear, isEditMode]);




  // Fetch customer data when customer changes
  useEffect(() => {
    if (formData.customer) {
      const selectedCustomer = customers.find(c => c.id === parseInt(formData.customer));
      if (selectedCustomer) {
        if (!isEditMode) {
          const resetData = getInitialFormState();
          setFormData(prev => ({
            ...resetData,
            customer: prev.customer,
            invoiceType: prev.invoiceType,
            financialYear: prev.financialYear,
            invoiceDate: prev.invoiceDate,
            invoiceNumber: prev.invoiceNumber,
            billingCustomerName: selectedCustomer.name,
            billingAddress: selectedCustomer.address,
            billingGST: selectedCustomer.gst_number || '',
            shippingCustomerName: selectedCustomer.name,
            shippingAddress: selectedCustomer.address,
            shippingGST: selectedCustomer.gst_number || '',
            contactPerson: selectedCustomer.contact_person_name || ''
          }));

          setFilmRateSummary([]);
          fetchUnbilledReportsByCustomer(formData.customer);
        }
      }
    }
  }, [formData.customer, customers, isEditMode]);

  // Clear data when invoice type changes
  useEffect(() => {
    if (!isEditMode) {
      setFormData(prev => ({
        ...prev,
        selectedReports: [],
        siteWorkItems: [
          {
            id: 1,
            date: '',
            description: '',
            quantity: 1,
            rate: 0
          }
        ]
      }));
      setFilmRateSummary([]);
    }
  }, [formData.invoiceType]);

  //Watch for changes in showOnlySelected
  useEffect(() => {
    if (isEditMode && formData.customer && formData.selectedReports && formData.selectedReports.length > 0) {
      fetchReportsForEdit(formData.customer, formData.selectedReports, formData.showOnlySelected);
    }
  }, [formData.showOnlySelected]);

  // Fetch next invoice number
  const fetchNextInvoiceNumber = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/invoices/next-number`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { financialYear: formData.financialYear }
        }
      );

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          invoiceNumber: response.data.nextNumber
        }));
      }
    } catch (err) {
      console.error('Error fetching next invoice number:', err);
    }
  };


  //Fetch missing invoice numbers
  const fetchMissingInvoiceNumbers = async (financialYear) => {
    try {
      const yearToFetch = financialYear || formData.financialYear;

      if (!yearToFetch) {
        console.warn('No financial year provided for fetching missing numbers');
        return;
      }

      console.log('🔍 Fetching missing invoice numbers for:', yearToFetch); // Debug log

      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/invoices/missing-numbers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { financialYear: yearToFetch }
        }
      );

      if (response.data.success) {
        console.log('Missing numbers received:', response.data.missingNumbers); // Debug log
        setMissingNumbers(response.data.missingNumbers || []);
      }
    } catch (err) {
      console.error('Error fetching missing invoice numbers:', err);
      setMissingNumbers([]);
    }
  };

  // Validate invoice number format
  //const validateInvoiceNumber = (invoiceNumber) => {
    //const pattern = new RegExp(`^PNS\\/INV\\/${formData.financialYear}\\/\\d+$`);
    //return pattern.test(invoiceNumber);
  //};

const validateInvoiceNumber = (invoiceNumber) => {
  if (!invoiceNumber) return false;
  const pattern = new RegExp(`^PNS/${formData.financialYear}/\\d+$`);
  return pattern.test(invoiceNumber.trim());
};


  const fetchCustomers = async () => {
    try {
      const token = Cookies.get('token');
      // const response = await axios.get('https://app-pune-ndt.cloud/api/customers', {
      const response = await axios.get('https://app-pune-ndt.cloud/api/customers/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchReports = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/reports', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  // Fetch ONLY unbilled reports for new invoice
  const fetchUnbilledReportsByCustomer = async (customerId) => {
    try {
      setNoUnbilledReportsMessage('');
      const token = Cookies.get('token');
      const response = await axios.get(`https://app-pune-ndt.cloud/api/reports/filter`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          customer: customerId,
          status: 'Unbilled',
          limit: 1000
        }
      });
      if (response.data.success) {
        const unbilledReports = response.data.data;

        if (unbilledReports.length === 0) {
          setNoUnbilledReportsMessage('No unbilled reports found for this customer. Please create reports first or select a different customer.');
          setFilteredReports([]);
        } else {
          setNoUnbilledReportsMessage('');
          setFilteredReports(unbilledReports);
        }
      }
    } catch (err) {
      console.error('Error fetching unbilled reports:', err);
      setNoUnbilledReportsMessage('❌ Error fetching reports. Please try again.');
    }
  };

  const fetchFilmSizes = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/film-sizes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setFilmSizes(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching film sizes:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/employees', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  //Fetch invoice data for edit mode
  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/invoices/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const invoice = response.data.data;

        console.log('Fetched Invoice Data:', invoice);
        console.log('Invoice Date from DB:', invoice.invoice_date);
        console.log('Selected Reports from Backend:', invoice.selectedReports);
        console.log('Selected Reports Length:', invoice.selectedReports?.length);

        //Format dates properly (UTC to avoid -1 day issue)
        const formattedSelectedDates = invoice.selectedDates
          ? invoice.selectedDates.map(date => formatDateForInput(date))
          : [];

        // Format invoice date properly
        let formattedInvoiceDate = new Date().toISOString().split('T')[0];
        if (invoice.invoice_date) {
          formattedInvoiceDate = formatDateForInput(invoice.invoice_date);
        }

        console.log('Formatted Invoice Date:', formattedInvoiceDate);

        const newFormData = {
          // Basic Info
          customer: invoice.customer_id,
          invoiceType: invoice.invoice_type,
          financialYear: invoice.financial_year,
          selectedReports: invoice.selectedReports || [],
          invoiceDate: formattedInvoiceDate,
          jobName: invoice.job_name || '',
          poNumber: invoice.po_number || '',
          soNumber: invoice.so_number || '',
          projectNumber: invoice.project_number || '',
          invoiceNumber: invoice.invoice_number,
          contactPerson: invoice.contact_person || '',
          noteRemark: invoice.note_remark || '',
          cgst: invoice.cgst || 9,
          sgst: invoice.sgst || 9,

          // Billing Address
          billingCustomerName: invoice.billing_customer_name || '',
          billingAddress: invoice.billing_address || '',
          billingGST: invoice.billing_gst || '',
          billingState: invoice.billing_state || 'Maharashtra',

          // Shipping Address
          shippingCustomerName: invoice.shipping_customer_name || '',
          shippingAddress: invoice.shipping_address || '',
          shippingGST: invoice.shipping_gst || '',
          shippingState: invoice.shipping_state || 'Maharashtra',

          // Convenience Charges
          minChargesQty: invoice.min_charges_qty || '',
          minChargesRate: invoice.min_charges_rate || '',
          minChargesHSN: invoice.min_charges_hsn || '998346',
          numberOfVisits: invoice.number_of_visits || '',
          perVisitRate: invoice.per_visit_rate || '',
          visitsHSN: invoice.visits_hsn || '998346',

          // Date-based Charges with formatted dates
          selectedDates: formattedSelectedDates,
          datesQuantity: invoice.datesQuantity || '1',
          datesCharges: invoice.datesCharges || '',
          datesHSN: invoice.datesHSN || '998346',

          // Other Charges
          otherCharges: invoice.otherCharges || [{ id: 1, chargeName: '', quantity: 1, rate: 0 }],

          // Site Work Items
          siteWorkItems: invoice.siteWorkItems || [{ id: 1, date: '', description: '', quantity: 1, rate: 0 }],

          // Selected Reports
          selectedReports: invoice.selectedReports || [],
          showOnlySelected: true,

          // Report Filters
          filterPO: '',
          filterMSN: '',
          filterTag: '',
          filterDrawing: '',
          filterSSN: '',
          filterProject: '',
          filterSO: '',
          filterJobId: '',
          filterReportNumber: '',
          filterFinancialYear: '',
          filterCreatedBy: '',
          filterFromDate: '',
          filterToDate: ''
        };

        console.log('Setting formData with selectedReports:', newFormData.selectedReports);

        setFormData(newFormData);

        // Fetch reports for edit mode
        if (invoice.customer_id && invoice.selectedReports) {
          console.log('   Calling fetchReportsForEdit with:');
          console.log('   Customer ID:', invoice.customer_id);
          console.log('   Selected Reports:', invoice.selectedReports);
          console.log('   Show Only Selected: true');
          await fetchReportsForEdit(invoice.customer_id, invoice.selectedReports, true);
        }
      }
    } catch (err) {
      setError('Failed to fetch invoice data');
      console.error('Error fetching invoice:', err);
    } finally {
      setLoading(false);
    }
  };



  //datepicker extra date picking solution whn youscrol to next mmonth

  const [tempDate, setTempDate] = useState('');

  const handleDateChange = (value) => {
    setTempDate(value);
  };


  // Add this function - it only adds dates, doesn't touch datesQuantity
  const handleDateAdd = () => {
    if (!tempDate || formData.selectedDates.includes(tempDate)) {
      setTempDate('');
      return;
    }

    setFormData(prev => ({
      ...prev,
      selectedDates: [...prev.selectedDates, tempDate].sort()
      // datesQuantity is NOT modified here - it stays at its fixed value
    }));

    setTempDate('');
  };


  // Fetch reports for edit mode with proper filtering
  const fetchReportsForEdit = async (customerId, selectedReportIds, showOnlySelected) => {
    try {
      console.log('🔍 fetchReportsForEdit called with:');
      console.log('   Customer ID:', customerId);
      console.log('   Selected Report IDs:', selectedReportIds);
      console.log('   Show Only Selected:', showOnlySelected);

      const token = Cookies.get('token');

      // Fetch ALL reports for this customer
      const response = await axios.get(`https://app-pune-ndt.cloud/api/reports/filter`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          customer: customerId,
          limit: 10000
        }
      });

      if (response.data.success) {
        const allReports = response.data.data;

        console.log('All Reports Count:', allReports.length);

        let filtered = [];

        if (showOnlySelected) {
          // Show ONLY selected reports (can be Billed)
          console.log('Filtering mode: Only Selected Reports');

          filtered = allReports.filter(r => selectedReportIds.includes(r.id));

          console.log('After filtering:', filtered.length, 'reports matched');
        } else {
          // Show selected reports (Billed) + unbilled reports for this customer
          console.log('Filtering mode: Selected + Unbilled Reports');

          filtered = allReports.filter(r =>
            selectedReportIds.includes(r.id) || r.status === 'Unbilled'
          );
        }

        console.log('Final Filtered Reports Count:', filtered.length);

        setFilteredReports(filtered);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Error fetching reports for edit:', err);
    }
    // 21/01/2026 this code will display only unbilled reports when creating new invoice
  };



  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Validate invoice number if manually entered
    if (name === 'invoiceNumber' && value) {
      if (!validateInvoiceNumber(value)) {
        setError(`Invoice number must be in format: PNS/${formData.financialYear}/NUMBER`);
      } else {
        setError('');
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateSelection = (date) => {
    setFormData(prev => {
      const dates = [...prev.selectedDates];
      const index = dates.indexOf(date);

      if (index > -1) {
        dates.splice(index, 1);
      } else {
        dates.push(date);
      }

      return {
        ...prev,
        selectedDates: dates.sort()
      };
    });
  };


  const removeSelectedDate = (dateToRemove) => {
    setFormData(prev => ({
      ...prev,
      selectedDates: prev.selectedDates.filter(date => date !== dateToRemove)
      // datesQuantity remains unchanged
    }));
  };

  // Other Charges Handlers
  const addOtherCharge = () => {
    setFormData(prev => ({
      ...prev,
      otherCharges: [
        ...prev.otherCharges,
        {
          id: prev.otherCharges.length + 1,
          chargeName: '',
          quantity: 1,
          rate: 0
        }
      ]
    }));
  };

  const removeOtherCharge = (index) => {
    if (formData.otherCharges.length === 1) {
      alert('At least one item is required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      otherCharges: prev.otherCharges.filter((_, i) => i !== index)
    }));
  };

  const handleOtherChargeChange = (index, field, value) => {
    const updatedCharges = [...formData.otherCharges];
    updatedCharges[index][field] = value;

    setFormData(prev => ({
      ...prev,
      otherCharges: updatedCharges
    }));
  };

  // Site Work Item Handlers
  const addSiteWorkItem = () => {
    setFormData(prev => ({
      ...prev,
      siteWorkItems: [
        ...prev.siteWorkItems,
        {
          id: prev.siteWorkItems.length + 1,
          date: '',
          description: '',
          quantity: 1,
          rate: 0
        }
      ]
    }));
  };

  const removeSiteWorkItem = (index) => {
    if (formData.siteWorkItems.length === 1) {
      alert('At least one item is required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      siteWorkItems: prev.siteWorkItems.filter((_, i) => i !== index)
    }));
  };

  const handleSiteWorkItemChange = (index, field, value) => {
    const updatedItems = [...formData.siteWorkItems];
    updatedItems[index][field] = value;

    setFormData(prev => ({
      ...prev,
      siteWorkItems: updatedItems
    }));
  };

  // Report Selection Handlers
  const handleReportSelection = (reportId) => {
    setFormData(prev => {
      const selected = [...prev.selectedReports];
      const index = selected.indexOf(reportId);

      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(reportId);
      }

      return {
        ...prev,
        selectedReports: selected
      };
    });
  };

  // Handle Filter Reports - NOW CALLS BACKEND
  const handleFilterReports = async () => {
    try {
      const token = Cookies.get('token');

      // Build query params
      const params = {
        customer: formData.customer,
        limit: 10000 // Get all reports
      };

      // Different behavior for new vs edit mode
      if (!isEditMode) {
        // NEW INVOICE MODE: Show ONLY Unbilled reports
        params.status = 'Unbilled';
      } else {
        // EDIT MODE: Handle based on showOnlySelected checkbox
        if (formData.showOnlySelected) {
          // Show ONLY reports in this invoice (can be Billed)
          // Don't add status filter - we'll filter by selected IDs
        } else {
          // Show invoice reports (Billed) + ALL Unbilled reports
          // Don't add status filter here - we'll handle it after fetching
        }
      }

      // Add other filters
      if (formData.filterPO) params.po_no = formData.filterPO;
      if (formData.filterMSN) params.msn_no = formData.filterMSN;
      if (formData.filterTag) params.tag_no = formData.filterTag;
      if (formData.filterDrawing) params.drawing_no = formData.filterDrawing;
      if (formData.filterSSN) params.ssn_no = formData.filterSSN;
      if (formData.filterProject) params.project_no = formData.filterProject;
      if (formData.filterSO) params.so_no = formData.filterSO;
      if (formData.filterJobId) params.jobJointId = formData.filterJobId;
      if (formData.filterReportNumber) params.reportNumber = formData.filterReportNumber;
      if (formData.filterFinancialYear) params.financialYear = formData.filterFinancialYear;
      if (formData.filterCreatedBy) params.created_by = formData.filterCreatedBy;
      if (formData.filterFromDate) params.fromDate = formData.filterFromDate;
      if (formData.filterToDate) params.toDate = formData.filterToDate;

      const response = await axios.get('https://app-pune-ndt.cloud/api/reports/filter', {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      if (response.data.success) {
        let filtered = response.data.data;

        if (isEditMode) {
          // EDIT MODE LOGIC
          if (formData.showOnlySelected) {
            // Show ONLY selected reports (regardless of status)
            const selectedReportIds = formData.selectedReports || [];
            filtered = filtered.filter(r => selectedReportIds.includes(r.id));
          } else {
            // Show selected reports (Billed) + ALL Unbilled reports
            const selectedReportIds = formData.selectedReports || [];

            // Filter to show: selected reports OR unbilled reports
            filtered = filtered.filter(r =>
              selectedReportIds.includes(r.id) || r.status === 'Unbilled'
            );
          }
        }
        // For NEW invoice mode, filtered already contains only Unbilled (from params.status)

        if (!isEditMode && filtered.length === 0) {
          setNoUnbilledReportsMessage('⚠️ No unbilled reports found matching the filters. Please adjust filters or create new reports.');
        } else {
          setNoUnbilledReportsMessage('');
        }

        setFilteredReports(filtered);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Error filtering reports:', err);
      setNoUnbilledReportsMessage('❌ Error filtering reports. Please try again.');
    }
  };

  // Handle Clear Filters
  const handleClearFilters = () => {
    setFormData(prev => ({
      ...prev,
      filterPO: '',
      filterMSN: '',
      filterTag: '',
      filterDrawing: '',
      filterSSN: '',
      filterProject: '',
      filterSO: '',
      filterJobId: '',
      filterReportNumber: '',
      filterFinancialYear: '',
      filterCreatedBy: '',
      filterFromDate: '',
      filterToDate: '',
      showOnlySelected: isEditMode
    }));

    if (isEditMode) {
      fetchReportsForEdit(formData.customer, formData.selectedReports, isEditMode);
    } else {
      fetchUnbilledReportsByCustomer(formData.customer);
    }
  };


  // Get Film Rate Summary
  const getFilmRateSummary = async () => {
    const summary = [];

    for (const reportId of formData.selectedReports) {
      try {
        const token = Cookies.get('token');

        const reportResponse = await axios.get(
          `https://app-pune-ndt.cloud/api/reports/${reportId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (reportResponse.data.success) {
          const report = reportResponse.data.data;
          const filmDetails = report.filmDetails || [];

          const filmGroups = {};

          filmDetails.forEach(film => {
            const filmSize = film.film_size_display || film.film_size;

            if (!filmGroups[filmSize]) {
              filmGroups[filmSize] = {
                filmSize: filmSize,
                filmSizeId: film.film_size_id,
                qty: 0,
                reshoot: 0,
                rate: 0
              };
            }

            filmGroups[filmSize].qty++;

            if (film.remark === 'Reshoot') {
              filmGroups[filmSize].reshoot++;
            }
          });

          for (const filmSize in filmGroups) {
            const filmSizeId = filmGroups[filmSize].filmSizeId;

            const rateResponse = await axios.get(
              `https://app-pune-ndt.cloud/api/film-rates/customer/${formData.customer}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (rateResponse.data.success) {
              const rates = rateResponse.data.data;
              const rateData = rates.find(r => r.film_size_id === filmSizeId);

              if (rateData) {
                filmGroups[filmSize].rate = parseFloat(rateData.rate_per_film);
              } else {
                filmGroups[filmSize].rate = 0;
              }
            }

            const effectiveQty = filmGroups[filmSize].qty - filmGroups[filmSize].reshoot;
            filmGroups[filmSize].totalByRate = effectiveQty * filmGroups[filmSize].rate;

            summary.push({
              reportNumber: report.report_number,
              ...filmGroups[filmSize]
            });
          }
        }
      } catch (err) {
        console.error('Error fetching film rate summary:', err);
      }
    }

    return summary;
  };

  const [filmRateSummary, setFilmRateSummary] = useState([]);

  // Update film rate summary based on conditions
  useEffect(() => {
    const hasMinCharges = formData.minChargesRate && parseFloat(formData.minChargesRate) > 0;

    if (formData.selectedReports.length > 0 && formData.invoiceType === 'Film' && !hasMinCharges) {
      getFilmRateSummary().then(summary => {
        setFilmRateSummary(summary);
      });
    } else {
      setFilmRateSummary([]);
    }
  }, [formData.selectedReports, formData.invoiceType, formData.minChargesRate]);

  //Handle Submit with Validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate invoice number is not empty
    if (!formData.invoiceNumber || formData.invoiceNumber.trim() === '') {
      setError('Invoice number is required. Please enter or select an invoice number.');
      setLoading(false);
      messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validation for Film type - at least one report must be selected
    if (formData.invoiceType === 'Film') {
      if (!formData.selectedReports || formData.selectedReports.length === 0) {
        setError('Please select at least one report to create or update the invoice');
        setLoading(false);
        messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    // Validation for Site type - at least one site work item must be added
    if (formData.invoiceType === 'Site') {
      const validSiteWork = formData.siteWorkItems?.filter(item =>
        item.description && item.description.trim() !== ''
      );

      if (!validSiteWork || validSiteWork.length === 0) {
        setError('Please add at least one site work to create or update the invoice');
        setLoading(false);
        messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    // Validate invoice number format
    //if (formData.invoiceNumber && !validateInvoiceNumber(formData.invoiceNumber)) {
      //setError(`Invalid invoice number format. Must be: PNS/${formData.financialYear}/NUMBER`);
      //setLoading(false);
      //messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      //return;
    //}

if (!isEditMode && formData.invoiceNumber && !validateInvoiceNumber(formData.invoiceNumber)) {
  setError(`Invalid invoice number format. Must be: PNS/${formData.financialYear}/NUMBER`);
  setLoading(false);
  messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return;
}

    try {
      const token = Cookies.get('token');
      const url = isEditMode
        ? `https://app-pune-ndt.cloud/api/invoices/${id}`
        : 'https://app-pune-ndt.cloud/api/invoices';

      const method = isEditMode ? 'put' : 'post';

      const payload = {
        ...formData
      };

      const response = await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(isEditMode ? 'Invoice updated successfully!' : 'Invoice created successfully!');
        messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          navigate('/invoices');
        }, 1500);
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || 'Failed to save invoice');
      }
      console.error('Error saving invoice:', err);
      console.error('Error details:', err.response?.data);
      messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Pagination - Show all reports or paginate properly
  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);

  // Handle Select All (across all pages)
  const handleSelectAllReports = (checked) => {
    if (checked) {
      const allReportIds = filteredReports.map(r => r.id);
      setFormData(prev => ({
        ...prev,
        selectedReports: allReportIds
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedReports: []
      }));
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-xl p-8 mb-6 border-l-4 border-indigo-900">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {isViewMode ? 'View Invoice' : isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
              </h1>
              <p className="text-gray-600 text-lg">
                {isViewMode ? 'Invoice details and information' : isEditMode ? 'Update invoice information and details' : 'Fill in the details below to generate a new invoice'}
              </p>
            </div>
            <button
              onClick={() => navigate('/invoices')}
              className="bg-indigo-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              ← Back to Invoices
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 text-green-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{success}</span>
            </div>
          </div>
        )}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}

        {/* Display message if no unbilled reports found */}
        {!isEditMode && noUnbilledReportsMessage && (
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-orange-500 text-orange-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{noUnbilledReportsMessage}</span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-xl p-8 mb-6 border-t-4 border-indigo-900">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-indigo-900 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">1</span>
              Basic Information
            </h2>

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={customers}
                  value={formData.customer}
                  onChange={(id) => handleInputChange({ target: { name: 'customer', value: id } })}
                  placeholder="Select Customer"
                  disabled={isViewMode || isEditMode}
                />
                {isEditMode && (
                  <p className="text-xs text-orange-600 mt-1 font-medium">
                    ⚠️ Customer cannot be changed in edit mode
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="invoiceType"
                  value={formData.invoiceType}
                  onChange={handleInputChange}
                  required
                  disabled={isViewMode}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                >
                  <option value="Film">Film</option>
                  <option value="Site">Site</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Financial Year <span className="text-red-500">*</span>
                </label>
                <select
                  name="financialYear"
                  value={formData.financialYear}
                  onChange={handleInputChange}
                  required
                  disabled={isViewMode}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                >
                  {financialYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {isEditMode && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleInputChange}
                  required
                  disabled={isViewMode}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Job Name
                </label>
                <input
                  type="text"
                  name="jobName"
                  value={formData.jobName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Enter job name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  PO Number
                </label>
                <input
                  type="text"
                  name="poNumber"
                  value={formData.poNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Enter PO number"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SO Number
                </label>
                <input
                  type="text"
                  name="soNumber"
                  value={formData.soNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Enter SO number"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Number
                </label>
                <input
                  type="text"
                  name="projectNumber"
                  value={formData.projectNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Enter project number"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>
            </div>

            {/*Display missing numbers as clickable hints */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  required
                  disabled={isViewMode}
                  placeholder="e.g., PNS/2025-2026/1"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
                {missingNumbers.length > 0 && !isViewMode && (
                  <div className="mt-2">
                    <p className="text-xs text-indigo-900 font-semibold mb-1">
                      Missing invoice numbers (click to use):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {missingNumbers.slice(0, 5).map((num) => {
                        const missingInvoiceNumber = `PNS/${formData.financialYear}/${num}`;
                        return (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, invoiceNumber: missingInvoiceNumber }))}
                            className="px-3 py-1 text-xs font-semibold bg-blue-50 text-red-600 rounded-full hover:bg-gray-50 transition-all duration-200 border border-indigo-500"
                            title="Click to use this number"
                          >
                            {missingInvoiceNumber}
                          </button>
                        );
                      })}
                      {missingNumbers.length > 5 && (
                        <span className="px-3 py-1 text-xs font-semibold text-gray-500">
                          +{missingNumbers.length - 5} more...
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {isEditMode && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Contact person name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  CGST (%)
                </label>
                <input
                  type="number"
                  name="cgst"
                  value={formData.cgst}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SGST (%)
                </label>
                <input
                  type="number"
                  name="sgst"
                  value={formData.sgst}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>
            </div>

            {/* Row 4 - Note/Remark */}
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Note / Remark
                </label>
                <textarea
                  name="noteRemark"
                  value={formData.noteRemark}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows="3"
                  placeholder="Add any notes, remarks, or tips..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          {/* 4 Vertical Sections - Billing, Shipping, Convenience, Other Details */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            {/* Billing Address */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-900">
              <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
                Billing Address
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    name="billingCustomerName"
                    value={formData.billingCustomerName}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    name="billingAddress"
                    value={formData.billingAddress}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    rows="3"
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    GST Number
                  </label>
                  <input
                    type="text"
                    name="billingGST"
                    value={formData.billingGST}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    State
                  </label>
                  <select
                    name="billingState"
                    value={formData.billingState}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 transition-all duration-200"
                  >
                    {indianStates.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-900">
              <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                </svg>
                Shipping Address
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    name="shippingCustomerName"
                    value={formData.shippingCustomerName}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    name="shippingAddress"
                    value={formData.shippingAddress}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    rows="3"
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    GST Number
                  </label>
                  <input
                    type="text"
                    name="shippingGST"
                    value={formData.shippingGST}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    State
                  </label>
                  <select
                    name="shippingState"
                    value={formData.shippingState}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 transition-all duration-200"
                  >
                    {indianStates.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Convenience Charges */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-900">
              <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
                Convenience Charges
              </h3>

              <div className="space-y-5">
                {/* Min Charges */}
                <div className="border-b-2 border-gray-200 pb-4">
                  <p className="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide">Minimum Charges</p>
                  {formData.minChargesRate && parseFloat(formData.minChargesRate) > 0 && (
                    <div className="mb-2 p-2 bg-orange-100 border border-red-200 rounded text-xs text-red-600">
                      ⚠️ Film rates will not be applied when minimum charges are set
                    </div>
                  )}
                  <div className="space-y-2">
                    <input
                      type="number"
                      name="minChargesQty"
                      value={formData.minChargesQty}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      placeholder="Quantity"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                    <input
                      type="number"
                      name="minChargesRate"
                      value={formData.minChargesRate}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      placeholder="Rate (₹)"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                    <input
                      type="text"
                      name="minChargesHSN"
                      value={formData.minChargesHSN}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      placeholder="HSN/SAC"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Number of Visits */}
                <div className="border-b-2 border-gray-200 pb-4">
                  <p className="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide">Visits</p>
                  <div className="space-y-2">
                    <input
                      type="number"
                      name="numberOfVisits"
                      value={formData.numberOfVisits}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      readOnly={true}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                    <input
                      type="number"
                      name="perVisitRate"
                      value={formData.perVisitRate}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      readOnly={true}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                    <input
                      type="text"
                      name="visitsHSN"
                      value={formData.visitsHSN}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      readOnly={true}
                      placeholder="HSN/SAC"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Other Details - Date-based Charges and Other Charges */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-900">
              <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-900" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Other Details
              </h3>

              <div className="space-y-5">
                {/* Date-based Charges */}
                <div className="border-b-2 border-gray-200 pb-4">
                  <p className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wide">Date-based Charges</p>
                  <div className="space-y-2">
                    {/* Date Picker with Add Button */}
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={tempDate}
                        onChange={(e) => setTempDate(e.target.value)}
                        disabled={isViewMode}
                        className="flex-1 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 transition-all duration-200"
                      />
                      <button
                        type="button"
                        onClick={handleDateAdd}
                        disabled={isViewMode || !tempDate}
                        className="px-4 py-2 text-sm bg-indigo-900 text-white rounded-lg hover:bg-indigo-800 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap transition-all duration-200 font-medium"
                      >
                        Add Date
                      </button>
                    </div>

                    {/* Selected Dates Display */}
                    {formData.selectedDates.length > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg text-xs space-y-1 max-h-24 overflow-y-auto border border-indigo-300">
                        {formData.selectedDates.map(date => (
                          <div key={date} className="flex justify-between items-center bg-white px-2 py-1 rounded">
                            <span className="font-medium text-gray-700">
                              {date.split('-').reverse().join('-')}
                            </span>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => removeSelectedDate(date)}
                                className="text-red-600 hover:text-red-800 font-bold text-lg"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quantity (Read-only) */}
                    <input
                      type="number"
                      name="datesQuantity"
                      value={formData.datesQuantity}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      readOnly
                      placeholder="Quantity"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 bg-gray-50 cursor-not-allowed transition-all duration-200"
                    />

                    {/* Charges */}
                    <input
                      type="number"
                      name="datesCharges"
                      value={formData.datesCharges}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      placeholder="Charges (₹)"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 transition-all duration-200"
                    />

                    {/* HSN/SAC */}
                    <input
                      type="text"
                      name="datesHSN"
                      value={formData.datesHSN}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      placeholder="HSN/SAC"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Other Charges Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-indigo-900 uppercase tracking-wide">Other Charges</p>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={addOtherCharge}
                        className="text-green-500 hover:text-indigo-700 font-bold text-xl"
                        title="Add Other Charge"
                      >
                        +
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {formData.otherCharges.map((charge, index) => (
                      <div key={charge.id} className="bg-blue-50 p-3 rounded-lg border border-indigo-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-600">#{index + 1}</span>
                          {!isViewMode && formData.otherCharges.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeOtherCharge(index)}
                              className="text-red-600 hover:text-red-800 font-bold text-lg"
                              title="Remove"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={charge.chargeName}
                            onChange={(e) => handleOtherChargeChange(index, 'chargeName', e.target.value)}
                            disabled={isViewMode}
                            placeholder="Charge name"
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              value={charge.quantity}
                              onChange={(e) => handleOtherChargeChange(index, 'quantity', e.target.value)}
                              disabled={isViewMode}
                              placeholder="Qty"
                              min="1"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                            />
                            <input
                              type="number"
                              value={charge.rate}
                              onChange={(e) => handleOtherChargeChange(index, 'rate', e.target.value)}
                              disabled={isViewMode}
                              placeholder="Rate"
                              min="0"
                              step="0.01"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                            />
                          </div>
                          <div className="text-xs font-semibold text-gray-700">
                            Amount: ₹{(charge.quantity * charge.rate).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.otherCharges.length > 0 && (
                    <div className="mt-3 p-2 bg-green-100 rounded-lg text-sm font-bold text-gray-900">
                      Total: ₹{formData.otherCharges.reduce((sum, c) => sum + (c.quantity * c.rate), 0).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Reports Section - Show for BOTH Film and Site types */}
          {formData.customer && (
            <>
              {/* Report Filters */}
              <div className="bg-white rounded-xl shadow-xl p-8 mb-6 border-t-4 border-indigo-600">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                  Invoice Reports
                  <span className="ml-3 text-sm font-normal text-gray-600">
                    {isEditMode
                      ? (formData.showOnlySelected ? '(Invoice Reports Only)' : '(Invoice + Unbilled)')
                      : '(Unbilled Reports Only)'}
                  </span>
                  {formData.invoiceType === 'Site' && (
                    <span className="ml-3 text-xs font-normal text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                      📌 Reports saved for statement purpose
                    </span>
                  )}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                  <input
                    type="text"
                    name="filterPO"
                    value={formData.filterPO}
                    onChange={handleInputChange}
                    placeholder="PO Number"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterMSN"
                    value={formData.filterMSN}
                    onChange={handleInputChange}
                    placeholder="MSN No"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterTag"
                    value={formData.filterTag}
                    onChange={handleInputChange}
                    placeholder="Tag No"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterDrawing"
                    value={formData.filterDrawing}
                    onChange={handleInputChange}
                    placeholder="Drawing Number"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterSSN"
                    value={formData.filterSSN}
                    onChange={handleInputChange}
                    placeholder="SSN Number"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterProject"
                    value={formData.filterProject}
                    onChange={handleInputChange}
                    placeholder="Project No"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterSO"
                    value={formData.filterSO}
                    onChange={handleInputChange}
                    placeholder="SO No"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type="text"
                    name="filterJobId"
                    value={formData.filterJobId}
                    onChange={handleInputChange}
                    placeholder="Job ID"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />

                  <input
                    type="text"
                    name="filterReportNumber"
                    value={formData.filterReportNumber}
                    onChange={handleInputChange}
                    placeholder="Report Number"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />

                  <select
                    name="filterFinancialYear"
                    value={formData.filterFinancialYear}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  >
                    <option value="">All Years</option>
                    {financialYears.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <select
                    name="filterCreatedBy"
                    value={formData.filterCreatedBy}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  >
                    <option value="">All Employees</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>

                  <input
                    type={formData.filterFromDate ? 'date' : 'text'}
                    name="filterFromDate"
                    value={formData.filterFromDate}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      e.target.type = 'date';
                      e.target.showPicker?.();
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.type = 'text';
                    }}
                    placeholder="From Date"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                  <input
                    type={formData.filterToDate ? 'date' : 'text'}
                    name="filterToDate"
                    value={formData.filterToDate}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      e.target.type = 'date';
                      e.target.showPicker?.();
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.type = 'text';
                    }}
                    placeholder="To Date"
                    disabled={isViewMode}
                    className="px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm transition-all duration-200"
                  />
                </div>

                <div className="flex items-center justify-between">
                  {isEditMode && (
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="showOnlySelected"
                        checked={formData.showOnlySelected}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Only Selected Reports
                        <span className="block text-xs text-gray-500 font-normal">
                          {formData.showOnlySelected
                            ? 'Showing only reports in this invoice'
                            : 'Showing invoice reports + unbilled reports'}
                        </span>
                      </span>
                    </label>
                  )}

                  {!isEditMode && (
                    <div className="text-sm text-gray-600 font-medium">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Showing only Unbilled reports
                      </span>
                    </div>
                  )}

                  <div className="space-x-3">
                    <button
                      type="button"
                      onClick={handleFilterReports}
                      disabled={isViewMode}
                      className="bg-indigo-900 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 shadow-lg disabled:opacity-50"
                    >
                      🔍 Filter
                    </button>
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      disabled={isViewMode}
                      className="bg-gray-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-600 transition-all duration-200 shadow-lg disabled:opacity-50"
                    >
                      ✖️ Clear
                    </button>
                  </div>
                </div>
              </div>

              {/* Reports Table */}
              <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-indigo-900 to-indigo-900 text-white">
                      <tr>
                        <th className="px-4 py-4 text-left text-sm font-bold">
                          {/* Select All checkbox */}
                          <input
                            type="checkbox"
                            onChange={(e) => handleSelectAllReports(e.target.checked)}
                            checked={filteredReports.length > 0 && filteredReports.every(r => formData.selectedReports.includes(r.id))}
                            disabled={isViewMode}
                            className="w-5 h-5"
                            title="Select all reports (across all pages)"
                          />
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Sr. No</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Report Number</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">PO Number</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Project No</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">SO Number</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Job ID</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Created By</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Report Date</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Generated Date</th>
                        <th className="px-4 py-4 text-left text-sm font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentReports.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="px-4 py-12 text-center text-gray-500">
                            <div className="flex flex-col items-center">
                              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p className="text-lg font-semibold">No reports found</p>
                              <p className="text-sm text-gray-400">
                                {isEditMode
                                  ? 'Try unchecking "Only Selected Reports" or adjust your filters'
                                  : 'No unbilled reports available for this customer'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentReports.map((report, index) => (
                          <tr key={report.id} className="hover:bg-indigo-50 transition-colors duration-150">
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={formData.selectedReports.includes(report.id)}
                                onChange={() => handleReportSelection(report.id)}
                                disabled={isViewMode}
                                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-700">
                              {indexOfFirstReport + index + 1}
                            </td>
                            <td className="px-4 py-4 text-sm font-bold text-indigo-600">
                              {report.report_number}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-700">{report.po_no || 'N/A'}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{report.project_no || 'N/A'}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{report.so_no || 'N/A'}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{report.job_joint_id}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{report.created_by_name || 'N/A'}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{formatDate(report.report_date)}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">{formatDate(report.created_at)}</td>
                            <td className="px-4 py-4">
                              <span className={`px-3 py-1 text-xs font-bold rounded-full ${report.status === 'Billed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {report.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/*  Pagination */}
                {totalPages > 1 && (
                  <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">
                      Showing {indexOfFirstReport + 1} to {Math.min(indexOfLastReport, filteredReports.length)} of {filteredReports.length} reports
                      {formData.selectedReports.length > 0 && (
                        <span className="ml-3 text-indigo-600 font-bold">
                          ({formData.selectedReports.length} selected)
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 font-semibold transition-all duration-200"
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          type="button"
                          onClick={() => setCurrentPage(i + 1)}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${currentPage === i + 1
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 font-semibold transition-all duration-200"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Film Rate Summary - Only for Film type AND no minimum charges */}
              {formData.invoiceType === 'Film' && formData.selectedReports.length > 0 && !(formData.minChargesRate && parseFloat(formData.minChargesRate) > 0) && (
                <div className="bg-white rounded-xl shadow-xl p-8 mb-6 border-t-4 border-green-600">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                    Film Rate Summary
                    <span className="ml-3 text-sm font-normal text-gray-600">
                      ({formData.selectedReports.length} report{formData.selectedReports.length > 1 ? 's' : ''} selected)
                    </span>
                    {filmRateSummary.length > 0 && (
                      <span className={`ml-3 text-sm font-semibold px-3 py-1 rounded-full ${[...new Set(filmRateSummary.map(item => item.filmSize))].length > 15
                        ? 'text-red-700 bg-red-50 border border-red-300'
                        : 'text-green-700 bg-green-50'
                        }`}>
                        Unique Film Sizes: {[...new Set(filmRateSummary.map(item => item.filmSize))].length}
                      </span>
                    )}
                  </h2>

                  {/* Warning message if unique film sizes exceed 15 */}
                  {filmRateSummary.length > 0 && [...new Set(filmRateSummary.map(item => item.filmSize))].length > 15 && (
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-orange-500 text-orange-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
                      <div className="flex items-center">
                        <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold">Unique Film Sizes exceeds the limit of 15. This will affect the statement layout in PDF</span>
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto"></div>




                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-green-50 to-green-100 border-b-2 border-green-500">
                        <tr>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Sr. No</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Report Number</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Film Size</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Qty</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Reshoot</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Rate (₹)</th>
                          <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Total by Rate (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filmRateSummary.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                              <div className="animate-pulse flex flex-col items-center">
                                <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <p className="font-semibold">Loading film rate summary...</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filmRateSummary.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                              <td className="px-4 py-4 text-sm font-medium text-gray-700">{index + 1}</td>
                              <td className="px-4 py-4 text-sm font-bold text-indigo-600">{item.reportNumber}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 font-medium">{item.filmSize}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 font-semibold">{item.qty}</td>
                              <td className="px-4 py-4 text-sm text-red-600 font-bold">{item.reshoot}</td>
                              <td className="px-4 py-4 text-sm font-bold">
                                {item.rate > 0 ? (
                                  <span className="text-gray-900">₹{item.rate.toFixed(2)}</span>
                                ) : (
                                  <span className="text-red-600 bg-red-50 px-2 py-1 rounded">Rate not Set</span>
                                )}
                              </td>
                              <td className="px-4 py-4 text-sm font-bold">
                                {item.totalByRate > 0 ? (
                                  <span className="text-green-700">₹{item.totalByRate.toFixed(2)}</span>
                                ) : (
                                  <span className="text-gray-500">N/A</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                        {filmRateSummary.length > 0 && (
                          <tr className="bg-gradient-to-r from-green-100 to-green-200 font-bold">
                            <td colSpan="6" className="px-4 py-4 text-right text-base text-gray-900">
                              Grand Total:
                            </td>
                            <td className="px-4 py-4 text-base text-green-700 font-bold">
                              ₹{filmRateSummary.reduce((sum, item) => sum + (item.totalByRate || 0), 0).toFixed(2)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Site/Other Work Section (Only for Site type) */}
          {formData.invoiceType === 'Site' && (
            <div className="bg-white rounded-xl shadow-xl p-8 mb-6 border-t-4 border-indigo-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <span className="bg-indigo-800 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                  Site/Other Work
                </h2>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={addSiteWorkItem}
                    className="bg-indigo-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 shadow-lg"
                  >
                    + Add Item
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 to-indigo-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Sr. No</th>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Date</th>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Description</th>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Quantity</th>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Rate (₹)</th>
                      <th className="px-4 py-4 text-left text-sm font-bold text-gray-700">Amount (₹)</th>
                      {!isViewMode && (
                        <th className="px-4 py-4 text-center text-sm font-bold text-gray-700">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-300">
                    {formData.siteWorkItems.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-4 py-4 text-sm font-medium text-gray-700">{index + 1}</td>
                        <td className="px-4 py-4">
                          <input
                            type="text"
                            value={item.date}
                            onChange={(e) => handleSiteWorkItemChange(index, 'date', e.target.value)}
                            disabled={isViewMode}
                            placeholder="DD-MM-YYYY"
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm disabled:bg-gray-100 transition-all duration-200"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleSiteWorkItemChange(index, 'description', e.target.value)}
                            disabled={isViewMode}
                            placeholder="Work description"
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm disabled:bg-gray-100 transition-all duration-200"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleSiteWorkItemChange(index, 'quantity', e.target.value)}
                            disabled={isViewMode}
                            min="1"
                            className="w-24 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm disabled:bg-gray-100 transition-all duration-200"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleSiteWorkItemChange(index, 'rate', e.target.value)}
                            disabled={isViewMode}
                            min="0"
                            step="0.01"
                            className="w-32 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm disabled:bg-gray-100 transition-all duration-200"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <span className="font-bold text-gray-900 text-base">
                            ₹{(item.quantity * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        {!isViewMode && (
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => removeSiteWorkItem(index)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-full font-bold text-2xl transition-all duration-200"
                              title="Remove Item"
                            >
                              ×
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {formData.siteWorkItems.length > 0 && (
                      <tr className="bg-gradient-to-r from-blue-100 to-indigo-200 font-bold">
                        <td colSpan="5" className="px-4 py-4 text-right text-base text-gray-900">
                          Total Amount:
                        </td>
                        <td colSpan={!isViewMode ? "2" : "1"} className="px-4 py-4 text-base text-red-700 font-bold">
                          ₹{formData.siteWorkItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/*  Success/Error Messages ABOVE Action Buttons */}
          <div ref={messageRef}>
            {success && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 text-green-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
                <div className="flex items-center">
                  <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">{success}</span>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-lg mb-6 shadow-md animate-fade-in">
                <div className="flex items-center">
                  <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">{error}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/invoices')}
                className="bg-gray-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-indigo-900 to-indigo-700 text-white px-8 py-4 rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center">
                    {isEditMode ? '💾 Update Invoice' : '✨ Create Invoice'}
                  </span>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
export default NewInvoice;